import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { X, Download, ImageOff, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../utils/cn";

// Visor de imagen a pantalla completa (helpdesk + evidencia de entrega).
// Mismo contrato visual que ui/Modal: backdrop con --overlay-backdrop
// (oscurece, jamás blur), niveles z 1200/1201, scroll-lock del body,
// Esc / click en backdrop cierran y el focus vuelve al disparador.
//
// Dos modos, RETRO-COMPATIBLES entre sí:
//   1) `url`    → una sola imagen (uso histórico del chat de helpdesk).
//   2) `images` (array) + `initialIndex` → galería con flechas ‹ ›, teclas
//      ← →  y contador "n de N". Con una sola imagen en el array el render es
//      idéntico al modo 1 (sin flechas ni contador).

// Zoom del visor: 1x = ajustado a pantalla; el primer click/tecla salta a 2.5x
// (suficiente para leer un número de casa o una firma) y el máximo es 4x.
const ZOOM_CLICK = 2.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 1.4;
const ZOOM_IDENTITY = { scale: 1, x: 0, y: 0 };
// Movimiento máximo (px) para que un press cuente como click y no como arrastre.
const DRAG_THRESHOLD = 5;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function MediaLightbox({
  url,
  images,
  initialIndex = 0,
  caption,
  alt,
  onClose,
  // Variante "profile": foto de perfil de contacto. Bordes muy redondeados,
  // sombra/glow sutil sobre el backdrop oscuro y tamaño máximo más contenido
  // (no llena la pantalla como un adjunto). Emula WhatsApp Web.
  profile = false,
}) {
  const { t } = useTranslation();
  const closeBtnRef = useRef(null);
  const reduced = useMemo(() => prefersReducedMotion(), []);
  // Media histórica con el objeto S3 corrupto: el <img> falla al decodificar y
  // sin este estado el alt se desparramaba sobre el backdrop.
  const [broken, setBroken] = useState(false);

  // `images` manda; si no viene, la lista es el `url` de siempre.
  const list = useMemo(() => {
    const arr = Array.isArray(images) ? images.filter(Boolean) : [];
    if (arr.length) return arr;
    return url ? [url] : [];
  }, [images, url]);
  const total = list.length;

  // Zoom + paneo: transform `translate(x,y) scale(s)` con origen en el centro.
  // Así el desplazamiento que mantiene bajo el cursor el punto ampliado es
  // t' = d - (d - t) * s2 / s (d = cursor respecto al centro) y el clamp es
  // simétrico. Se aplica en un wrapper para no pisar la animación animate-scale-in
  // del <img> (su transform se pisaría mientras dura la animación).
  const [zoom, setZoom] = useState(ZOOM_IDENTITY);
  const [dragging, setDragging] = useState(false);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  // Un arrastre termina en un `click` sintético: este flag evita que alterne zoom.
  const movedRef = useRef(false);
  const zoomed = zoom.scale > 1;
  const canZoom = !profile;

  const [index, setIndex] = useState(() => Number(initialIndex) || 0);
  const safeIndex = Math.min(Math.max(index, 0), Math.max(total - 1, 0));
  const current = list[safeIndex];
  const multi = total > 1;

  // Cada imagen arranca "sana" y sin zoom: si la anterior estaba rota o
  // ampliada, no arrastrar el estado.
  useEffect(() => {
    setBroken(false);
    setZoom(ZOOM_IDENTITY);
  }, [current]);

  // Cambia la escala manteniendo bajo el cursor el punto ampliado. `centerX/Y`
  // es el centro del rect actual del <img> (ya incluye el translate vigente);
  // sin coordenadas (teclado) se amplía respecto al centro.
  const applyZoom = useCallback(
    (nextScale, clientX, clientY, centerX, centerY) => {
      setZoom((z) => {
        const s2 = Math.min(Math.max(nextScale(z.scale), 1), ZOOM_MAX);
        if (s2 < 1.02) return ZOOM_IDENTITY;
        const dx = clientX == null ? 0 : clientX - (centerX - z.x);
        const dy = clientY == null ? 0 : clientY - (centerY - z.y);
        const k = s2 / z.scale;
        // offsetWidth/Height ignoran el transform: son el tamaño "ajustado".
        const w = imgRef.current?.offsetWidth || 0;
        const h = imgRef.current?.offsetHeight || 0;
        const limX = (w * (s2 - 1)) / 2;
        const limY = (h * (s2 - 1)) / 2;
        return {
          scale: s2,
          x: Math.min(Math.max(dx - (dx - z.x) * k, -limX), limX),
          y: Math.min(Math.max(dy - (dy - z.y) * k, -limY), limY),
        };
      });
    },
    [],
  );

  // Esc + flechas + scroll-lock (patrón de ui/Modal).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      // Ctrl/Cmd con + - 0 es el zoom del navegador, y quien escribe en un campo
      // (p. ej. el compositor del helpdesk) no debe perder esas teclas.
      const el = e.target;
      const typing =
        el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ""));
      if (!profile && !typing && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.key === "+" || e.key === "=") {
          e.preventDefault();
          applyZoom((s) => (s <= 1 ? ZOOM_CLICK : s * ZOOM_STEP));
          return;
        }
        if (e.key === "-") {
          e.preventDefault();
          applyZoom((s) => s / ZOOM_STEP);
          return;
        }
        if (e.key === "0") {
          e.preventDefault();
          setZoom(ZOOM_IDENTITY);
          return;
        }
      }
      if (total < 2) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setIndex((i) => Math.min(i + 1, total - 1));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, total, profile, applyZoom]);

  // Focus inicial al botón cerrar; al desmontar, devolver el focus.
  useEffect(() => {
    const previouslyFocused =
      typeof document !== "undefined" ? document.activeElement : null;
    const id = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(id);
      if (previouslyFocused && previouslyFocused.focus) {
        try {
          previouslyFocused.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, []);

  if (typeof document === "undefined" || !current) return null;

  const onImgClick = (e) => {
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    if (zoomed) {
      setZoom(ZOOM_IDENTITY);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    applyZoom(
      () => ZOOM_CLICK,
      e.clientX,
      e.clientY,
      r.left + r.width / 2,
      r.top + r.height / 2,
    );
  };

  const onImgWheel = (e) => {
    // El scroll del body ya está bloqueado; la rueda solo ajusta el zoom.
    const r = e.currentTarget.getBoundingClientRect();
    // Proporcional a deltaY: un trackpad manda decenas de eventos por gesto y un
    // factor fijo por evento saltaba a 4x de golpe.
    const f = Math.exp(-e.deltaY * 0.002);
    applyZoom(
      (s) => s * f,
      e.clientX,
      e.clientY,
      r.left + r.width / 2,
      r.top + r.height / 2,
    );
  };

  const onImgPointerDown = (e) => {
    movedRef.current = false;
    if (!zoomed || (e.pointerType === "mouse" && e.button !== 0)) return;
    dragRef.current = {
      id: e.pointerId,
      sx: e.clientX,
      sy: e.clientY,
      ox: zoom.x,
      oy: zoom.y,
    };
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* puntero ya liberado: el arrastre sigue por eventos del elemento */
    }
  };

  const onImgPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    const mx = e.clientX - d.sx;
    const my = e.clientY - d.sy;
    if (!movedRef.current && Math.hypot(mx, my) <= DRAG_THRESHOLD) return;
    movedRef.current = true;
    setDragging(true);
    // Clamp: la imagen nunca sale del todo de la pantalla (borde ampliado
    // como máximo al borde del encuadre ajustado).
    const limX = ((imgRef.current?.offsetWidth || 0) * (zoom.scale - 1)) / 2;
    const limY = ((imgRef.current?.offsetHeight || 0) * (zoom.scale - 1)) / 2;
    setZoom((z) => ({
      ...z,
      x: Math.min(Math.max(d.ox + mx, -limX), limX),
      y: Math.min(Math.max(d.oy + my, -limY), limY),
    }));
  };

  const onImgPointerEnd = (e) => {
    if (dragRef.current?.id !== e.pointerId) return;
    dragRef.current = null;
    setDragging(false);
  };

  const zoomHint = zoomed
    ? t("Haz clic para reducir")
    : t("Haz clic para ampliar");

  // El centrado vertical va en un WRAPPER, nunca en el <button>: el press global
  // (`button:active { transform: scale(.97) }` de _micro-interactions.scss)
  // sobreescribe la propiedad `transform`, así que un `-translate-y-1/2` puesto
  // en el propio botón se pierde al presionar y el botón "salta" ~18 px fuera
  // del puntero → el click se cae. Con el translate en el div contenedor, el
  // press del botón solo aplica su scale y el botón no se mueve.
  const navBtn =
    "inline-flex w-9 h-9 items-center justify-center rounded-md bg-black/40 text-white hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/40";

  const node = (
    <div className="fixed inset-0" style={{ zIndex: 1200 }}>
      {/* Backdrop — oscurece con el token, sin blur. */}
      <div
        className={cn("absolute inset-0", !reduced && "animate-fade-in")}
        style={{ background: "var(--overlay-backdrop)" }}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={alt || caption || t("Imagen ampliada")}
        className="absolute inset-0 flex flex-col items-center justify-center p-4"
        style={{ zIndex: 1201 }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        {/* Acciones arriba-derecha */}
        {/* z-10: con zoom el wrapper transformado de la imagen se pinta encima y tapaba la X. */}
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          <a
            href={current}
            download
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("Descargar imagen")}
            title={t("Descargar imagen")}
            className="inline-flex w-9 h-9 items-center justify-center rounded-md bg-black/40 text-white hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            <Download className="w-4 h-4" aria-hidden="true" />
          </a>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t("Cerrar")}
            title={t("Cerrar")}
            className="inline-flex w-9 h-9 items-center justify-center rounded-md bg-black/40 text-white hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Navegación de galería — solo con 2+ imágenes. El centrado va en el
            wrapper (ver comentario de `navBtn`). */}
        {multi && (
          <>
            <div className="absolute inset-y-0 left-3 z-10 flex items-center pointer-events-none">
              <button
                type="button"
                onClick={() => setIndex((i) => Math.max(i - 1, 0))}
                disabled={safeIndex === 0}
                aria-label={t("Imagen anterior")}
                title={t("Imagen anterior")}
                className={cn(navBtn, "pointer-events-auto")}
              >
                <ChevronLeft className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
            <div className="absolute inset-y-0 right-3 z-10 flex items-center pointer-events-none">
              <button
                type="button"
                onClick={() => setIndex((i) => Math.min(i + 1, total - 1))}
                disabled={safeIndex === total - 1}
                aria-label={t("Imagen siguiente")}
                title={t("Imagen siguiente")}
                className={cn(navBtn, "pointer-events-auto")}
              >
                <ChevronRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </>
        )}

        {broken ? (
          <div className="flex flex-col items-center gap-3 px-8 py-10 rounded-lg bg-black/40 text-white">
            <ImageOff className="w-8 h-8 opacity-80" aria-hidden="true" />
            <p className="text-sm text-white/90">
              {t("No se pudo cargar la imagen")}
            </p>
          </div>
        ) : (
          <div
            className="inline-flex"
            onClick={canZoom ? onImgClick : undefined}
            onWheel={canZoom ? onImgWheel : undefined}
            onPointerDown={canZoom ? onImgPointerDown : undefined}
            onPointerMove={canZoom ? onImgPointerMove : undefined}
            onPointerUp={canZoom ? onImgPointerEnd : undefined}
            onPointerCancel={canZoom ? onImgPointerEnd : undefined}
            style={
              canZoom
                ? {
                    transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
                    // Ampliada, touch-action none: si no, el navegador táctil se queda
                    // con el gesto y no llegan los pointermove del arrastre. Sin zoom
                    // se deja el pellizco nativo del navegador.
                    touchAction: zoomed ? "none" : "manipulation",
                    cursor: dragging ? "grabbing" : zoomed ? "grab" : "zoom-in",
                    transition:
                      reduced || dragging ? "none" : "transform 150ms ease-out",
                  }
                : undefined
            }
          >
            <img
              ref={imgRef}
              key={current}
              src={current}
              alt={alt || caption || t("Imagen")}
              draggable={false}
              onError={() => setBroken(true)}
              className={cn(
                "object-contain select-none",
                !reduced && "animate-scale-in",
                // Perfil: foto de contacto con bordes muy redondeados (WhatsApp
                // style), sombra blanca sutil sobre el backdrop oscuro y tamaño
                // máximo más contenido para que no llene la pantalla.
                profile
                  ? "max-w-[min(480px,88vw)] max-h-[min(480px,80vh)] rounded-3xl shadow-[0_0_0_1px_rgba(255,255,255,0.08),0_8px_32px_rgba(0,0,0,0.4)]"
                  : "max-w-[92vw] max-h-[86vh] rounded-lg dark-safe-img",
              )}
            />
          </div>
        )}

        {canZoom && !broken && (
          <p className="mt-2 text-xs text-white/70">{zoomHint}</p>
        )}

        {caption && (
          <p className="mt-3 max-w-[92vw] text-sm text-white/90 text-center break-words whitespace-pre-wrap">
            {caption}
          </p>
        )}

        {multi && (
          <p
            className="mt-3 text-xs font-semibold tracking-wide text-white/80 tabular-nums"
            aria-live="polite"
          >
            {t("{{current}} de {{total}}", { current: safeIndex + 1, total })}
          </p>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
